import { CardContent, Separator } from '@ais-ui/ui-core';
import { AtonTargetInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import { useLengthUnits } from '../../Units/useLengthUnits';

interface AtonInfoTooltipProps {
  atonInfo: AtonTargetInfo;
}

interface AtonInfoTooltipItemProps {
  label: string;
  value: string;
}

const AtonInfoTooltipItem = ({ label, value }: AtonInfoTooltipItemProps) => {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 max-w-[12rem]">
      <p className="text-xs font-medium flex-none text-left max-w-[6rem]">
        {label}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 flex-grow text-right">
        {value ? value : '-'}
      </p>
    </div>
  );
};

export const AtonInfoTooltip = ({ atonInfo }: AtonInfoTooltipProps) => {
  const { settings } = useSettings();
  const ribbonSettings = settings['ribbonPlugin.settings'];
  const { convertLengthString } = useLengthUnits();

  // Check if any of the settings are enabled and corresponding data exists
  const hasVisibleItems =
    (ribbonSettings?.show_aton_assigned_mode && atonInfo.assignedMode) ||
    (ribbonSettings?.show_aton_type && atonInfo.atonType) ||
    (ribbonSettings?.show_target_type && atonInfo.targetType) ||
    (ribbonSettings?.show_aton_country && atonInfo.country) ||
    (ribbonSettings?.show_aton_latitude && atonInfo.latitude) ||
    (ribbonSettings?.show_aton_longitude && atonInfo.longitude) ||
    (ribbonSettings?.show_aton_length && atonInfo.length) ||
    (ribbonSettings?.show_aton_mmsi && atonInfo.mmsi) ||
    (ribbonSettings?.show_aton_name && atonInfo.name) ||
    (ribbonSettings?.show_aton_off_position && atonInfo.offPosition) ||
    (ribbonSettings?.show_aton_state && atonInfo.state) ||
    (ribbonSettings?.show_aton_status && atonInfo.status) ||
    (ribbonSettings?.show_aton_width && atonInfo.width);

  // Return null if no items will be shown
  if (!hasVisibleItems) {
    return null;
  }

  return (
    <CardContent className="pt-0 pb-2 -mt-1 space-y-2 px-3">
      <Separator className="-ml-1"></Separator>
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        {ribbonSettings?.show_aton_assigned_mode && atonInfo.assignedMode && (
          <AtonInfoTooltipItem
            key="assignedMode"
            label="Assigned mode"
            value={atonInfo.assignedMode}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_type && atonInfo.atonType && (
          <AtonInfoTooltipItem
            key="atonType"
            label="AtoN Type"
            value={atonInfo.atonType}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_target_type && atonInfo.targetType && (
          <AtonInfoTooltipItem
            key="targetType"
            label="Target type"
            value={atonInfo.targetType}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_country && atonInfo.country && (
          <AtonInfoTooltipItem
            key="country"
            label="Country"
            value={atonInfo.country}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_latitude && atonInfo.latitude && (
          <AtonInfoTooltipItem
            key="latitude"
            label="Latitude"
            value={atonInfo.latitude.toString()}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_longitude && atonInfo.longitude && (
          <AtonInfoTooltipItem
            key="longitude"
            label="Longitude"
            value={atonInfo.longitude.toString()}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_length && atonInfo.length && (
          <AtonInfoTooltipItem
            key="length"
            label="Length"
            value={convertLengthString(atonInfo.length)}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_mmsi && atonInfo.mmsi && (
          <AtonInfoTooltipItem
            key="mmsi"
            label="MMSI"
            value={atonInfo.mmsi}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_name && atonInfo.name && (
          <AtonInfoTooltipItem
            key="name"
            label="Name"
            value={atonInfo.name}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_off_position && atonInfo.offPosition && (
          <AtonInfoTooltipItem
            key="offPosition"
            label="Off position"
            value={atonInfo.offPosition}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_state && atonInfo.state && (
          <AtonInfoTooltipItem
            key="state"
            label="State"
            value={atonInfo.state}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_status && atonInfo.status && (
          <AtonInfoTooltipItem
            key="status"
            label="Status"
            value={atonInfo.status}
          ></AtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_aton_width && atonInfo.width && (
          <AtonInfoTooltipItem
            key="width"
            label="Width"
            value={convertLengthString(atonInfo.width)}
          ></AtonInfoTooltipItem>
        )}
      </div>
    </CardContent>
  );
};
